/** `mark`: a press-and-hold on the board turned into drawing (R5). */
export type HapticCue = 'correct' | 'wrong' | 'solved' | 'mark';

/** Android/Chromium vibration patterns, in milliseconds. */
export const vibrationPatterns: Record<HapticCue, number[]> = {
  correct: [12],
  wrong: [28, 70, 28],
  solved: [18, 60, 18, 60, 36],
  mark: [8],
};
/** iPhone taps per cue (each switch toggle is one light system tap). */
export const tapCounts: Record<HapticCue, number> = { correct: 1, wrong: 2, solved: 3, mark: 1 };

/**
 * Best-effort haptics. Never throws and never blocks chess.
 *
 * - Browsers with the Vibration API (Android) use `navigator.vibrate`.
 * - iPhone Safari has no Vibration API. Since iOS 18, toggling an
 *   `<input type="checkbox" switch>` during a user gesture plays the system's
 *   light switch tap, so a hidden, momentary switch gives a tap there.
 * - Everywhere else nothing happens.
 */
export class Haptics {
  private active = true;
  private generation = 0;
  private disposed = false;
  get enabled() {
    return this.active;
  }
  set enabled(value: boolean) {
    if (this.active !== value) this.generation++;
    this.active = value;
  }
  constructor(
    private doc: Document = document,
    private nav: Navigator = navigator,
    private schedule: (fn: () => void, ms: number) => unknown = (fn, ms) => setTimeout(fn, ms),
  ) {}
  get mode(): 'vibrate' | 'switch' | 'none' {
    try {
      if (typeof this.nav.vibrate === 'function') return 'vibrate';
      const coarse = this.doc.defaultView?.matchMedia?.('(pointer: coarse)').matches;
      return this.nav.maxTouchPoints > 0 || coarse ? 'switch' : 'none';
    } catch {
      return 'none';
    }
  }
  fire(cue: HapticCue) {
    const generation = ++this.generation;
    if (!this.enabled || this.disposed || this.doc.hidden) return;
    try {
      const mode = this.mode;
      if (mode === 'vibrate') this.nav.vibrate(vibrationPatterns[cue]);
      else if (mode === 'switch')
        for (let i = 0; i < tapCounts[cue]; i++)
          if (i === 0) this.tap();
          else
            this.schedule(() => {
              if (
                generation === this.generation &&
                this.enabled &&
                !this.disposed &&
                !this.doc.hidden
              )
                this.tap();
            }, i * 90);
    } catch {
      /* Haptics are decoration only. */
    }
  }
  private tap() {
    let label: HTMLLabelElement | undefined;
    try {
      label = this.doc.createElement('label');
      label.setAttribute('aria-hidden', 'true');
      label.style.display = 'none';
      label.dataset.haptic = '';
      const input = this.doc.createElement('input');
      input.type = 'checkbox';
      input.setAttribute('switch', '');
      input.tabIndex = -1;
      label.appendChild(input);
      this.doc.head.appendChild(label);
      label.click();
    } catch {
      /* Delayed platform taps must not interrupt play either. */
    } finally {
      label?.remove();
    }
  }
  dispose() {
    this.disposed = true;
    this.generation++;
  }
}
